import { AxiosInstance, AxiosResponse } from "axios";

/** One IPSLA operation of an uplink, joined with its Influx metrics. */
export interface IpslaTarget {
    operationId: string;
    targetIp: string;
    targetLabel: string;
    targetAsn: string | null;
    /** False when the catalog row has no Influx series in the window. */
    hasData: boolean;
    /**
     * Lost probes in the window (Cisco IP SLA semantics: 0 = perfect,
     * 1 = one packet lost). Null when metrics were unavailable.
     */
    lostPackets: number | null;
    /** Probe samples received in the window. */
    sampleCount: number;
    lastRttMs: number | null;
    lastSeen: string | null;
}

/**
 * One backbone uplink with its monitored targets.
 *
 * `uplinkLabel` is an alias ("Provider B QRO"), never the carrier: the view
 * is open to partners in production and which carrier the transit is bought
 * from is not theirs to read. The API used to send `originIp`, `vrf` and
 * `hostname` beside it and nothing here ever read them, which made the alias
 * decorative -- one whois on the origin address answers the very question it
 * exists to avoid. They are gone from the payload.
 *
 * `source` stays: it is our own router's name, and the key the history
 * endpoints are addressed by.
 */
export interface IpslaUplink {
    uplinkLabel: string;
    source: string;
    targets: IpslaTarget[];
}

export interface InternetIpMonitoringData {
    /** Lookback window (seconds) used for the packet loss calculation. */
    windowSeconds: number;
    /** False when InfluxDB was unreachable: the catalog is still returned. */
    metricsAvailable: boolean;
    uplinks: IpslaUplink[];
}

interface InternetIpMonitoringResponse {
    success: boolean;
    message: string;
    data: InternetIpMonitoringData | null;
}

/** GET /kontroledge/monitoring/internet-ip-monitoring */
export function getInternetIpMonitoringAPI(
    api: AxiosInstance
): Promise<AxiosResponse<InternetIpMonitoringResponse>> {
    return api.get("/kontroledge/monitoring/internet-ip-monitoring");
}

/**
 * Shared by both history tabs: a single range selector drives packet loss
 * and RTT. 7d matches the lookback of the RTT baseline.
 */
export type HistoryRange = "1h" | "6h" | "24h" | "7d";

export interface PacketLossHistoryPoint {
    time: string;
    /** Lost probes in the aggregation window; null when the window has no samples. */
    lost: number | null;
    /** Checks received in the aggregation window. */
    samples: number;
}

export interface PacketLossHistoryData {
    uplinkLabel: string;
    targetIp: string;
    targetLabel: string;
    range: HistoryRange;
    everySeconds: number;
    points: PacketLossHistoryPoint[];
}

interface PacketLossHistoryResponse {
    success: boolean;
    message: string;
    data: PacketLossHistoryData | null;
}

/** GET /kontroledge/monitoring/internet-ip-monitoring/history */
export function getPacketLossHistoryAPI(
    api: AxiosInstance,
    source: string,
    operationId: string,
    range: HistoryRange
): Promise<AxiosResponse<PacketLossHistoryResponse>> {
    return api.get("/kontroledge/monitoring/internet-ip-monitoring/history", {
        params: { source, operationId, range },
    });
}

export interface RttHistoryPoint {
    time: string;
    /** Mean RTT of the ANSWERED probes in the window; null when none were answered. */
    rttMs: number | null;
    /** Answered probes the mean was computed from. */
    samples: number;
}

/**
 * The chart's two reference lines. A tail percentile alone cannot say what
 * "normal" is: across the fleet it lands anywhere from 0.4% to 115% above
 * the median depending on the link, so the same line means something
 * different on every target. Together they bound the normal operating
 * range, and how wide that band is, is itself the diagnosis.
 */
export interface RttBaseline {
    /** Lookback of both percentiles, in days. */
    days: number;
    /** What the link normally does; null when it has no answered probe. */
    typicalMs: number | null;
    /** What counts as a bad window for this link; null likewise. */
    unusualMs: number | null;
    typicalPercentile: number;
    unusualPercentile: number;
}

export interface RttHistoryData {
    uplinkLabel: string;
    targetIp: string;
    targetLabel: string;
    range: HistoryRange;
    everySeconds: number;
    baseline: RttBaseline;
    points: RttHistoryPoint[];
}

interface RttHistoryResponse {
    success: boolean;
    message: string;
    data: RttHistoryData | null;
}

/** GET /kontroledge/monitoring/internet-ip-monitoring/rtt-history */
export function getRttHistoryAPI(
    api: AxiosInstance,
    source: string,
    operationId: string,
    range: HistoryRange
): Promise<AxiosResponse<RttHistoryResponse>> {
    return api.get("/kontroledge/monitoring/internet-ip-monitoring/rtt-history", {
        params: { source, operationId, range },
    });
}
